/**
 * Core API wrapper utilities for standardized error handling
 *
 * This module provides:
 * - Typed error classes that map to backend error responses
 * - Generic wrapper functions for consistent API call patterns
 * - Retry logic and configuration options
 * - Result types for better error handling in components
 * - Wrapped API instances with standardized error handling
 */

export * from './errors';
export * from './apiWrapper';

// Import wrapped APIs
import { activityApi } from './activityWrapper';
import { billingApi } from './billingWrapper';
import { familiesApi } from './familiesWrapper';
import { peopleApi } from './peopleWrapper';
import { petsApi } from './petsWrapper';
import { gedcomImportsApi } from './gedcomImportsWrapper';
import { gedcomExportApi } from './gedcomExportWrapper';
import { usersApi } from './usersWrapper';
import { treeApi } from './treeWrapper';
import { personFactsApi } from './personFactsWrapper';
import { personLifeEventsApi } from './personLifeEventsWrapper';
import { factTypesApi } from './factTypesWrapper';
import { eventTypesApi } from './eventTypesWrapper';
import { sourcesApi, citationsApi } from './sourcesWrapper';
import { relationshipsApi } from './relationshipsWrapper';
import { storiesApi } from './storiesWrapper';
import { suggestionsApi } from './suggestionsWrapper';
import { reviewApi } from './reviewWrapper';
import { memberRolesApi } from './memberRolesWrapper';
import { memberBlocksApi } from './memberBlocksWrapper';
import { contentRolesApi } from './contentRolesWrapper';
import { notificationsApi } from './notificationsWrapper';

// Export wrapped APIs
export {
  familiesApi,
  peopleApi,
  petsApi,
  gedcomImportsApi,
  gedcomExportApi,
  usersApi,
  treeApi,
  personFactsApi,
  personLifeEventsApi,
  factTypesApi,
  eventTypesApi,
  sourcesApi,
  citationsApi,
  relationshipsApi,
  activityApi,
  storiesApi,
  billingApi,
  suggestionsApi,
  reviewApi,
  memberRolesApi,
  memberBlocksApi,
  contentRolesApi,
  notificationsApi
};

// Main API export with standardized error handling
export const api = {
  families: familiesApi,
  relationships: relationshipsApi,
  people: peopleApi,
  pets: petsApi,
  gedcomImports: gedcomImportsApi,
  gedcomExport: gedcomExportApi,
  users: usersApi,
  tree: treeApi,
  personFacts: personFactsApi,
  personLifeEvents: personLifeEventsApi,
  factTypes: factTypesApi,
  eventTypes: eventTypesApi,
  sources: sourcesApi,
  citations: citationsApi,
  activity: activityApi,
  stories: storiesApi,
  billing: billingApi,
  suggestions: suggestionsApi,
  review: reviewApi,
  memberRoles: memberRolesApi,
  memberBlocks: memberBlocksApi,
  contentRoles: contentRolesApi,
  notifications: notificationsApi
};

// Default export for convenience
export default api;

// Re-export commonly used types for convenience
export type { ApiResult } from './errors';
export type { ApiWrapperConfig, RetryConfig } from './apiWrapper';
